import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { SharedComponentsConfiguration } from '../contexts/ConfigurationContext';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// Helper to parse JWT token and check expiration
const parseJwt = (token: string): Record<string, any> | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string, bufferSeconds = 60): boolean => {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp - bufferSeconds <= now;
};

// Request token refresh from native app (for embedded mode)
const requestEmbeddedTokenRefresh = (): void => {
  const message = { type: 'GEKKO_REQUEST_TOKEN_REFRESH' };

  // For iframe embedding (parent window)
  if (window.parent !== window) {
    window.parent.postMessage(message, '*');
  }

  // For React Native WebView
  if ((window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
  }

  // For iOS WKWebView with message handlers
  if ((window as any).webkit?.messageHandlers?.NativeApp) {
    (window as any).webkit.messageHandlers.NativeApp.postMessage(message);
  }

  // For Android WebView with JS interface
  if ((window as any).NativeApp?.requestTokenRefresh) {
    (window as any).NativeApp.requestTokenRefresh();
  }
};

// Validation error response format from the API
export interface ValidationErrorResponse {
  message: string;
  errors: Record<string, string>; // field path -> error message, "_" for general errors
}

interface RequestOptions<TRequestBody = unknown> {
  body?: TRequestBody;
  queryParams?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
}

class GekkoApi {
  private baseUrl: string;
  private environment: 'development' | 'production' | 'test';

  constructor(config: SharedComponentsConfiguration) {
    this.baseUrl = config.gekkoApiBaseUrl;
    this.environment = config.environment || 'production';

    if (!this.baseUrl) {
      throw new Error('GekkoApi requires gekkoApiBaseUrl in configuration');
    }
  }

  /**
   * Get authentication token
   */
  public async getAuthToken(_url?: string): Promise<string> {
    // Check if token is already stored in sessionStorage (for components mode)
    const storedToken = window.sessionStorage.getItem('gekko/authheader');
    if (storedToken) {
      return storedToken;
    }

    // Check for embedded tokens from parent window (iframe/webview mode)
    const embeddedTokensString = window.sessionStorage.getItem(
      'gekko/embeddedTokens'
    );
    if (embeddedTokensString) {
      try {
        const embeddedTokens = JSON.parse(embeddedTokensString);
        if (embeddedTokens.accessToken) {
          // Check if token is expired or about to expire
          if (isTokenExpired(embeddedTokens.accessToken)) {
            // Request refresh from native app
            requestEmbeddedTokenRefresh();
            // Still use the token - native app will inject new tokens via postMessage
            // and the next request will use the refreshed token
            console.warn(
              'Embedded token is expired, requesting refresh from native app'
            );
          }
          return `Bearer ${embeddedTokens.accessToken}`;
        }
      } catch (error) {
        console.error('Failed to parse embedded tokens:', error);
      }
    }

    try {
      const session = await fetchAuthSession();
      const tokens = session.tokens;

      if (!tokens) {
        throw new Error('No tokens available');
      }

      const currentTimeSeconds = Math.round(+new Date() / 1000);
      const idTokenExpire = tokens.idToken?.payload.exp || 0;

      if (idTokenExpire < currentTimeSeconds) {
        // Token expired, try to refresh
        try {
          const refreshedSession = await fetchAuthSession({
            forceRefresh: true,
          });
          if (refreshedSession.tokens?.accessToken) {
            const token = `Bearer ${refreshedSession.tokens.accessToken.toString()}`;
            console.log('Access token:', token);
            return token;
          } else {
            throw new Error('Failed to refresh token');
          }
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
          await signOut();
          window.location.reload();
          throw refreshError;
        }
      } else {
        // Token is still valid
        if (tokens.accessToken) {
          console.log('Access token:', tokens.accessToken.toString());
          return `Bearer ${tokens.accessToken.toString()}`;
        } else {
          throw new Error('No access token available');
        }
      }
    } catch (error) {
      console.error('Failed to get auth token:', error);
      throw error;
    }
  }

  /**
   * Get Gekko information token
   */
  private async getGekkoInfoToken(): Promise<string | undefined> {
    // Check for embedded tokens first (iframe mode)
    const embeddedTokensString = window.sessionStorage.getItem(
      'gekko/embeddedTokens'
    );
    if (embeddedTokensString) {
      try {
        // For embedded tokens, we might not have gekkoInformationToken
        // Return undefined to skip this header for embedded mode
        return undefined;
      } catch (error) {
        console.error('Failed to parse embedded tokens for gekko info:', error);
      }
    }

    try {
      const session = await fetchAuthSession();
      const gekkoToken =
        session.tokens?.accessToken?.payload.gekkoInformationToken;
      return typeof gekkoToken === 'string' ? gekkoToken : undefined;
    } catch (error) {
      console.error('Failed to get Gekko information token:', error);
      return undefined;
    }
  }

  /**
   * Generic request method
   */
  private async request<TResponseData = unknown, TRequestBody = unknown>(
    method: HttpMethod,
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    try {
      const url = this.buildUrl(
        endpoint,
        method === 'GET' ? options?.queryParams : undefined
      );

      // Get auth token
      const token = await this.getAuthToken(endpoint);
      const gekkoInfoToken = await this.getGekkoInfoToken();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        Accept: '*/*',
        'Access-Control-Allow-Origin': '*',
        Authorization: token,
        ...options?.headers,
      };

      // Add Gekko information token if available
      if (gekkoInfoToken) {
        headers.gekkoInformationToken = gekkoInfoToken;
      }

      const fetchOptions: RequestInit = {
        method,
        headers,
      };

      // Add body for non-GET requests
      if (method !== 'GET' && options?.body) {
        fetchOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, fetchOptions);

      // Handle 401 Unauthorized
      if (response.status === 401) {
        /*await signOut();
        window.location.reload();*/
        throw new GekkoApiError('Unauthorized - you have been signed out', 401);
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorBody;

        // Try to parse error response as JSON if possible
        try {
          errorBody = JSON.parse(errorText);
        } catch {
          errorBody = { message: errorText };
        }

        throw new GekkoApiError(
          `API error (${response.status}): ${errorText}`,
          response.status,
          errorBody
        );
      }

      // Network error handling similar to axios setup
      if (!response) {
        const errorMessage =
          this.environment === 'production'
            ? 'There seems to be some problems.\n Please try again or refresh the page.\n Also try Logout and then Login again.\n If you still get this error contact us'
            : 'Problems to communicate with server.';

        throw new GekkoApiError(errorMessage, 0);
      }

      // Check if response is empty or not JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return (await response.json()) as TResponseData;
      }

      return {} as TResponseData;
    } catch (error) {
      // If the error is already a GekkoApiError, rethrow it
      if (error instanceof GekkoApiError) {
        throw error;
      }

      // Otherwise, create a new GekkoApiError
      console.error(
        `API request failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );

      throw new GekkoApiError(
        error instanceof Error ? error.message : 'Unknown API error',
        500
      );
    }
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(
    endpoint: string,
    queryParams?: Record<string, string | number | boolean | undefined>
  ): string {
    // Handle relative URLs
    const fullUrl = endpoint.startsWith('http')
      ? endpoint
      : `${this.baseUrl}${
          endpoint.startsWith('/') ? endpoint : `/${endpoint}`
        }`;

    const url = new URL(fullUrl);

    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * GET request
   */
  public get<TResponseData = unknown>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<TResponseData> {
    return this.request<TResponseData>('GET', endpoint, options);
  }

  /**
   * POST request
   */
  public post<TResponseData = unknown, TRequestBody = unknown>(
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    return this.request<TResponseData, TRequestBody>('POST', endpoint, options);
  }

  /**
   * PUT request
   */
  public put<TResponseData = unknown, TRequestBody = unknown>(
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    return this.request<TResponseData, TRequestBody>('PUT', endpoint, options);
  }

  /**
   * PATCH request
   */
  public patch<TResponseData = unknown, TRequestBody = unknown>(
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    return this.request<TResponseData, TRequestBody>(
      'PATCH',
      endpoint,
      options
    );
  }

  /**
   * DELETE request
   */
  public delete<TResponseData = unknown, TRequestBody = unknown>(
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    return this.request<TResponseData, TRequestBody>(
      'DELETE',
      endpoint,
      options
    );
  }
}

export class GekkoApiError extends Error {
  httpStatus: number;
  body: any;
  constructor(message: string, httpStatus: number, body?: any) {
    super(message);
    this.name = 'GekkoApiError';
    this.httpStatus = httpStatus;
    this.body = body;
  }
}

// The gekkoApi instance will be initialized by the configuration provider
export let gekkoApi: GekkoApi;

// Function to initialize the API instance with configuration
export const initializeGekkoApi = (config: SharedComponentsConfiguration) => {
  gekkoApi = new GekkoApi(config);
  return gekkoApi;
};

// Export the class for creating custom instances
export { GekkoApi };
export default GekkoApi;
